// Shared by the React page and the lightweight local preview.
export function attachOthercrownMedia(root) {
  if (!root) return () => {};
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const connection = navigator.connection;
  const conserve = () => motion.matches || connection?.saveData || /(^|-)2g$/.test(connection?.effectiveType || '');
  const cleanups = [];
  root.querySelectorAll('[data-oc-clip]').forEach(figure => {
    const video = figure.querySelector('video');
    const status = figure.querySelector('[role="status"]');
    const label = video.dataset.label;
    let visible = false, userPaused = false, userStarted = false, failed = false, disposed = false, pending = false;
    let timer;
    const listen = (target, name, handler) => {
      target?.addEventListener?.(name, handler);
      cleanups.push(() => target?.removeEventListener?.(name, handler));
    };
    const update = () => {
      video.setAttribute('aria-label', `${failed ? 'Retry' : video.paused ? 'Play' : 'Pause'} ${label}`);
    };
    const clearLoading = () => { clearTimeout(timer); status.textContent = ''; };
    const loading = () => {
      if (!visible || failed) return;
      status.textContent = 'Loading clip…';
      clearTimeout(timer);
      timer = setTimeout(() => { if (!disposed && visible && (pending || !video.paused)) status.textContent = 'Still loading — you can keep reading.'; }, 10000);
    };
    const load = () => {
      if (!video.getAttribute('src')) { video.preload = 'auto'; video.src = video.dataset.src; video.load(); }
    };
    const allowed = () => visible && !document.hidden && !userPaused && (!conserve() || userStarted);
    const play = () => {
      if (pending || failed) return;
      load();
      video.muted = true;
      pending = true;
      loading();
      Promise.resolve(video.play()).then(() => {
        pending = false;
        if (disposed || !allowed()) video.pause();
      }).catch(() => {
        pending = false;
        if (disposed || failed) return;
        clearLoading();
        if (allowed() && !failed) {
          userPaused = true;
          status.textContent = 'Tap the clip to watch.';
          update();
        }
      });
    };
    const sync = () => {
      if (allowed() && video.paused) play();
      else if (!allowed()) { video.pause(); clearLoading(); }
      update();
    };
    video.controls = false;
    const togglePlayback = () => {
      if (failed) { failed = false; video.removeAttribute('src'); }
      if (!video.paused) { userPaused = true; video.pause(); clearLoading(); }
      else { userPaused = false; userStarted = true; visible = true; play(); }
      update();
    };
    listen(video, 'click', togglePlayback);
    listen(video, 'keydown', event => {
      if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); togglePlayback(); }
    });
    listen(video, 'playing', () => { clearLoading(); update(); });
    listen(video, 'pause', update);
    listen(video, 'waiting', loading);
    listen(video, 'stalled', loading);
    listen(video, 'error', () => { failed = true; clearLoading(); status.textContent = 'Clip unavailable. Tap the clip to retry.'; update(); });
    listen(document, 'visibilitychange', sync);
    const preferencesChanged = () => { userStarted = false; sync(); };
    listen(motion, 'change', preferencesChanged);
    listen(connection, 'change', preferencesChanged);
    if ('IntersectionObserver' in window) {
      const near = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting && !conserve()) { load(); near.disconnect(); }
      }, { rootMargin: '150px' });
      const onscreen = new IntersectionObserver(entries => {
        visible = entries[0].isIntersecting && entries[0].intersectionRatio >= 0.1;
        sync();
      }, { threshold: [0, 0.1] });
      near.observe(video);
      onscreen.observe(video);
      cleanups.push(() => { near.disconnect(); onscreen.disconnect(); });
    } else {
      const check = () => { const box = video.getBoundingClientRect(); visible = box.bottom > 0 && box.top < window.innerHeight; sync(); };
      listen(window, 'scroll', check);
      listen(window, 'resize', check);
      check();
    }
    update();
    cleanups.push(() => { disposed = true; clearTimeout(timer); video.pause(); });
  });
  return () => cleanups.forEach(cleanup => cleanup());
}
